import { DependencyList, useEffect, useState } from 'react'
import { RehydrationInProgressError } from '../utils/RehydrationInProgressError'

export type PollStatus =
  | 'idle'
  | 'loading'
  | 'rehydrating'
  | 'success'
  | 'error'

interface Options {
  enabled?: boolean
}

/**
 * Calls `fetcher` and exposes the result as `{ status, data, error }`. If the
 * fetcher throws `RehydrationInProgressError`, schedules a retry after the
 * upstream-supplied Retry-After cadence. Polls indefinitely until 200 — there
 * is no upper bound, since the backend is designed to converge and
 * `Retry-After` keeps cadence reasonable.
 *
 * Dependency cleanup aborts the in-flight request and cancels any pending
 * retry timeout.
 */
export const usePollOn202 = <T>(
  fetcher: (signal: AbortSignal) => Promise<T | undefined>,
  deps: DependencyList,
  options: Options = {}
) => {
  const [status, setStatus] = useState<PollStatus>(
    options.enabled === false ? 'idle' : 'loading'
  )
  const [data, setData] = useState<T | undefined>()
  const [error, setError] = useState<unknown>()

  useEffect(() => {
    if (options.enabled === false) {
      setStatus('idle')
      return
    }

    const controller = new AbortController()
    let retryTimeout: ReturnType<typeof setTimeout> | undefined

    const runFetch = () => {
      fetcher(controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return
          setData(result)
          setStatus('success')
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          if (e instanceof RehydrationInProgressError) {
            setStatus('rehydrating')
            retryTimeout = setTimeout(runFetch, e.retryAfterSeconds * 1000)
            return
          }
          setError(e)
          setStatus('error')
        })
    }

    setStatus('loading')
    runFetch()

    return () => {
      controller.abort()
      if (retryTimeout !== undefined) clearTimeout(retryTimeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { status, data, error }
}
